import { TranslatableField, transl8 } from './TranslatableField';
import { BillingInfoDto } from './BillingInfoDto';
import type { TaxRules } from '.';

export enum TransactionType {
  Charge = 'charge',
  Refund = 'refund',
}

export enum TransactionStatus {
  Concluded = 'concluded',
  Pending = 'pending',
  Canceled = 'canceled',
}

export enum PaymentMethod {
  Invoicing = 'invoicing',
  Paysafe = 'paysafe',
  Stripe = 'stripe',
  TouchNet = 'touchnet',
  Moneris = 'moneris',
  Publisoft = 'publisoft',
  Alipay = 'alipay',
  GlobalPayments = 'globalpayments',
  PayPal = 'paypal',
  PayPalCardFields = 'paypal-cardfields',
  PayPalButtons = 'paypal-buttons',
  Void = 'void',
  ForwardPayment = 'forwardpayment',
}

export enum CardType {
  Visa = 'visa',
  Amex = 'amex',
  MasterCard = 'mastercard',
  Discover = 'discover',
  JCB = 'jcb',
  Diners = 'diners',
  UnionPay = 'unionpay',
  ChinaUnionPay = 'chinaunionpay',
}

export interface TransactionItemDto {
  fieldName: TranslatableField;
  choiceName: TranslatableField;
  itemId: string;
  total: number;
  subTotal: number;
  discountedSubTotal: number;
  qst: number;
  gst: number;
  hst: number;
  vat: number;
  pst: number;
  taxRule?: TaxRules;
}

export interface TransactionDto {
  id: string;
  paymentGatewayId: string;
  type: TransactionType;
  nameOnCard: string;
  cardType: CardType | null;
  last4Digits: string;
  subTotal: number;
  total: number;
  profit: number;
  refunded: number;
  gst: number;
  qst: number;
  hst: number;
  vat: number;
  pst: number;
  itemsTotal: number;
  currency: string;
  userFirstName: string;
  userLastName: string;
  paidByFirstName: string;
  paidByLastName: string;
  status: TransactionStatus;
  paymentMethod: PaymentMethod;
  paymentGatewayFee: number;
  fee: number;
  feeGst: number;
  feeHst: number;
  feeQst: number;
  creationDate: string;
  lastUpdateDate: string;
  eventId: string;
  eventName: TranslatableField;
  items: TransactionItemDto[];
  billingInfo?: BillingInfoDto;
  couponCode?: string;
  couponCodeAmount?: number;
  couponCodeAmountType?: string;
  formEntryId?: string;
  passFeesToParticipant?: boolean;
}

export function generateUserFriendlyId(id: string): string {
  if (id == null) {
    return '---';
  }
  return id.split('-').slice(0, 2).join('-').toUpperCase();
}

export function generateItemsAsString(items: TransactionItemDto[], currency: string): string {
  if (items == null) {
    return '---';
  }
  return items
    .map(item => `${transl8(item.fieldName)}: ${transl8(item.choiceName)} (${formatPennies(item.total, currency)})`)
    .join(' + ');
}

export function formatPennies(amount: number, currency: string): string {
  return removeCurrencySpec(getNumberFormat(currency).format(amount / 100), currency);
}

export function formatMoney(amount: number, currency: string): string {
  return removeCurrencySpec(getNumberFormat(currency).format(amount), currency);
}

function removeCurrencySpec(amount: string, currency: string): string {
  // Do not remove currency symbol "R" for ZAR
  if (currency === 'ZAR') return amount;

  return amount
    .replace('CA', '') // CAD
    .replace('A', '') // AUD
    .replace('R', '') // BRL
    .replace('MX', '') // MXN
    .replace('US', ''); // USD
}

function getNumberFormat(currency: string): Intl.NumberFormat {
  switch (currency) {
    case 'PLN':
      return new Intl.NumberFormat('pl', { style: 'currency', currency });
    case 'ZAR':
      return new Intl.NumberFormat('en-ZA', { style: 'currency', currency });
    case 'CNY':
      return new Intl.NumberFormat('zh-CN', { style: 'currency', currency });
    default:
      return new Intl.NumberFormat(undefined, { style: 'currency', currency });
  }
}
